/**
 * Per-run usage summaries: the daemon publishes a small summary of every recent run's usage
 * inside the encrypted `machine` report stream (`run.usage` frame, source `usage`), so every
 * surface shows a run's cost without opening its transcript. Each revision is the machine's full
 * list of recent runs; the latest replaces the previous one. Never plaintext.
 *
 * Money follows the M15 rules: `reportedUsd` is what the provider CLI stated; `estimatedUsd` is
 * computed from tokens with the dated price table (only when nothing was reported); neither means
 * the cost is missing. Subagent usage is a subset of the run total and is reported separately.
 */
import { z } from 'zod';
import { ModelTier } from './model-profiles.js';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const RUN_USAGE_MAX_RUNS = 150;
export const UsageTokens = z.strictObject({
    input: Count,
    output: Count,
    cacheRead: Count,
    cacheWrite: Count,
    reasoning: Count.optional(),
});
export const RUN_USAGE_MAX_SUBAGENT_ITEMS = 50;
/** One subagent's footprint (additive; absent in older summaries). */
export const RunUsageSubagent = z.strictObject({
    /** The subagent's tool-call id. */
    id: z.string().min(1).max(128),
    model: z.string().min(1).max(120).optional(),
    tier: ModelTier.optional(),
    tokens: Count,
    /** Tool calls by kind (shell, read, edit, …). */
    toolCalls: z.record(z.string().max(32), Count),
    readsBeforeFirstEdit: Count,
    edits: Count,
});
export const RunUsageModel = z.strictObject({
    model: z.string().min(1).max(120),
    tokens: UsageTokens,
    reportedUsd: z.number().nonnegative().optional(),
});
/** One efficiency finding (G18). Evidence is exact; text is user-readable. */
export const EfficiencyFinding = z.strictObject({
    id: z.enum(['D1', 'D2', 'D3', 'D4']),
    severity: z.enum(['info', 'warn']),
    title: z.string().max(300),
    detail: z.string().max(1000),
    evidence: z
        .array(z.strictObject({
        kind: z.enum(['subagent', 'command', 'task']),
        ref: z.string().max(400),
        at: Ts.optional(),
    }))
        .max(20),
    estSavingTokens: Count.optional(),
    suggestion: z.string().max(600),
});
export const RUN_EFFICIENCY_MAX_FINDINGS = 8;
/** Findings plus the run's tokens by model tier (additive; absent in older summaries). */
export const RunEfficiency = z.strictObject({
    findings: z.array(EfficiencyFinding).max(RUN_EFFICIENCY_MAX_FINDINGS),
    tokensByTier: z.strictObject({ fast: Count, standard: Count, deep: Count, unknown: Count }),
});
export const RunUsageSummary = z.strictObject({
    /** Run id; rotated runs carry a `.rN` suffix (`nextRotationId`). */
    runId: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    agent: z.enum(['claude', 'codex']),
    /** Provider account the run used (`claude`, `codex:work`), when known. */
    accountId: z.string().min(1).max(160).optional(),
    /** Session totals by kind (already include subagents). */
    tokens: UsageTokens.optional(),
    reportedUsd: z.number().nonnegative().optional(),
    /** Table-based estimate; present only when nothing was reported and every model is priced. */
    estimatedUsd: z.number().nonnegative().optional(),
    models: z.array(RunUsageModel).max(8),
    /** Subagent usage, reported separately and NOT added to `tokens`. */
    subagents: z
        .strictObject({
        count: Count,
        tokens: Count,
        /** Per-subagent breakdown, largest tokens first (capped). */
        items: z.array(RunUsageSubagent).max(RUN_USAGE_MAX_SUBAGENT_ITEMS).optional(),
    })
        .optional(),
    /** G18 efficiency findings for the run; older readers ignore it. */
    efficiency: RunEfficiency.optional(),
    updatedAt: Ts,
});
export const RunUsageFrame = z.strictObject({
    type: z.literal('run.usage'),
    ts: Ts,
    runs: z.array(RunUsageSummary).max(RUN_USAGE_MAX_RUNS),
});
//# sourceMappingURL=run-usage.js.map